// Survival stats: health / hunger / thirst / blood / infection.
// Hunger & thirst tick down slowly (faster while sprinting); at zero they eat
// into health. Poison from bad food lands after a realistic onset delay.
// Wounds (wolf bites, PvP hits) bleed blood until patched with a bandage or
// duct tape; low blood saps health, and blood only regrows on a full belly.
// An infected's claws (or a dirty rag pressed into an open wound) can seed a
// WOUND INFECTION: silent for 5–10 minutes, then fever → shakes → sepsis.
// Antibiotics cure it at any stage; painkillers only quiet the symptoms.
// Spec: docs/INFECTED.md Z54 + Z125…Z131.

import { HurtFeed } from './hurt.mjs';

// Where a given slice of damage came from, for hurt.mjs's pooling. The
// continuous sources are tagged EXPLICITLY rather than left to the size
// heuristic, because they are the ones called 60 times a second: at that rate
// a per-call flash would strobe and a per-call sound would be a buzz, so they
// are pooled and released as one soft pulse per 2.5 HP.
// Frozen and module-level so the 60 Hz call sites allocate nothing.
export const DAMAGE_SRC = {
  hunger: Object.freeze({ kind: 'hunger', trickle: true }),
  thirst: Object.freeze({ kind: 'thirst', trickle: true }),
  blood: Object.freeze({ kind: 'blood', trickle: true }),
  infection: Object.freeze({ kind: 'infection', trickle: true }),
  // a bad mushroom lands as one blow, not a drip
  poison: Object.freeze({ kind: 'poison', trickle: false }),
};

// Onsets are performance.now()-based, exactly like the poison queue above it.
export const INFECTION = {
  onsetMin: 300, onsetMax: 600,  // s of incubation before the first symptom (Z54)
  ramp: 900,                     // s from first symptom to full severity
  dpsMin: 0.03, dpsMax: 0.09,    // HP/s, ramping — averages the spec's 0.06 (Z54)
  reinfectCut: 0.25,             // a second wound while incubating pulls onset 25 % closer
  reinfectSev: 0.10,             // …or worsens an illness already running
  shakeAt: 0.30,                 // severity where the hands start to shake
  criticalAt: 0.75,
  painkillers: 240,              // s of quieted symptoms (the damage does not stop)
  thirstMul: 1.6,                // fever burns water
  messageGap: 45,                // s between symptom nags
};

export class Stats {
  constructor(player, hud, audio) {
    this.player = player;
    this.hud = hud;
    this.audio = audio;
    this.health = 100;
    this.hunger = 100;   // 100 = full
    this.thirst = 100;   // 100 = hydrated
    this.blood = 100;    // 100 = full volume
    this.wounds = 0;     // open bleeding wounds
    this.poisons = [];   // { at, dmg, applied }
    // wound infection: null, or { at (onset, performance.now ms), sev 0..1,
    // stage, quietUntil } — see INFECTION above
    this.infection = null;
    this.shakes = 0;     // 0..1 tremor amplitude for HUD/camera consumers
    this._infMsgT = 0;
    this.noise = null;   // NoiseSystem, wired in main.js (death scream ping)
    this.dead = false;
    // damage feedback: severity, direction, pooling and pitch all live in
    // src/game/hurt.mjs so they can be tested in plain Node
    this.hurtFeed = new HurtFeed();
    // optional hook for a camera flinch — main.js already calls
    // camera.addRecoil() at three of the hit sites and nowhere else, so this
    // is where starvation, bleeding out and sepsis can reach it too
    this.onHurt = null;
  }

  // ---- wound infection (Z54) --------------------------------------------
  // A clawed wound, or a filthy rag stuffed into one. Incubates silently.
  infect(mul = 1) {
    if (this.dead) return false;
    const now = performance.now();
    if (this.infection) {
      // already carrying it: a fresh dose brings the fever forward / deepens it
      if (this.infection.sev > 0) this.infection.sev = Math.min(1, this.infection.sev + INFECTION.reinfectSev * mul);
      else this.infection.at = now + (this.infection.at - now) * (1 - INFECTION.reinfectCut * mul);
      return true;
    }
    const delay = (INFECTION.onsetMin + Math.random() * (INFECTION.onsetMax - INFECTION.onsetMin)) / Math.max(0.25, mul);
    this.infection = { at: now + delay * 1000, sev: 0, stage: 'incubating', quietUntil: 0 };
    // warn, and keyed: a second dirty rag inside the hold window counts on
    // the live row instead of pushing something else off the lane
    this.hud.flashMove?.('The wound looks dirty…', { sev: 'warn', key: 'infection-seeded' });
    return true;
  }

  // antibiotics — the only real cure, at any stage
  cureInfection() {
    if (!this.infection) return false;
    this.infection = null;
    this.shakes = 0;
    this.hud.flashMove?.('The fever breaks — the infection is beaten', { key: 'infection-cured' });
    return true;
  }

  // painkillers: the symptoms go quiet, the sepsis does not
  suppressSymptoms(seconds = INFECTION.painkillers) {
    if (!this.infection) return false;
    this.infection.quietUntil = performance.now() + seconds * 1000;
    return true;
  }

  get infected() { return !!this.infection; }
  get infectionSeverity() { return this.infection ? this.infection.sev : 0; }
  // one short string for a HUD icon / tooltip
  infectionLabel() {
    if (!this.infection) return null;
    return { incubating: 'Infected wound', fever: 'Fever', sick: 'Sick — shakes',
      critical: 'Sepsis' }[this.infection.stage] || 'Infected';
  }

  _infectionTick(dt) {
    const inf = this.infection;
    if (!inf) { this.shakes = Math.max(0, this.shakes - dt); return; }
    const now = performance.now();
    if (now < inf.at) return;                     // still incubating: no signs at all
    const first = inf.stage === 'incubating';
    inf.sev = Math.min(1, inf.sev + dt / INFECTION.ramp);
    const stage = inf.sev >= INFECTION.criticalAt ? 'critical'
      : inf.sev >= INFECTION.shakeAt ? 'sick' : 'fever';
    const changed = stage !== inf.stage;
    inf.stage = stage;
    // ramping damage: 0.03 → 0.09 HP/s, i.e. ~0.06 average (Z54)
    this.damage((INFECTION.dpsMin + (INFECTION.dpsMax - INFECTION.dpsMin) * inf.sev) * dt,
      DAMAGE_SRC.infection);
    // fever dehydrates, and the shakes spoil your aim (HUD/camera read this)
    this.thirst = Math.max(0, this.thirst - (100 / 1700) * (INFECTION.thirstMul - 1) * dt);
    this.shakes = inf.sev < INFECTION.shakeAt ? 0
      : Math.min(1, (inf.sev - INFECTION.shakeAt) / (1 - INFECTION.shakeAt));
    if (now < inf.quietUntil) return;             // painkillers: no nagging
    this._infMsgT -= dt;
    if (first || changed || this._infMsgT <= 0) {
      this._infMsgT = INFECTION.messageGap;
      // keyed by STAGE, not by message: a 45 s repeat nag counts on the row it
      // already has (×2, ×3), but fever → sick → sepsis is new news every
      // time and gets its own row at its own severity. Sepsis is the loudest
      // thing the toast lane can say and it can no longer be evicted by
      // pickup chatter.
      this.hud.flashMove?.(stage === 'critical' ? 'Sepsis — you need antibiotics NOW'
        : stage === 'sick' ? 'You are shaking with fever'
          : 'You are burning up — the wound is infected',
      { sev: stage === 'critical' ? 'crit' : 'warn', key: `infection-${stage}` });
      // the stage CHANGE earns its own cue; the repeat nags do not, because
      // the pooled hurt pulse out of damage() is already sounding every ~30 s
      // at these damage rates (see hurt.mjs TRICKLE_GAP)
      if (first || changed) {
        const o = { vol: 0.35, rate: stage === 'critical' ? 0.9 : 1.05 };
        const sfx = this.audio?.sfx;
        if (sfx?.play && !sfx.play('ui.hurt', o)) sfx.play('player.hurt', o);
      }
    }
  }

  // An open wound starts bleeding (capped — a mauling can't stack forever).
  // IT ONLY SPEAKS WHEN THE WOUND COUNT ACTUALLY WENT UP. At the cap a wolf
  // pack landing a bite a second pushed "You are bleeding!" into the lane
  // over and over for a wound the player had not taken, which is how the
  // sepsis warning used to get evicted. Returns true when it was real news.
  wound(n = 1) {
    if (this.dead) return false;
    const before = this.wounds;
    this.wounds = Math.min(5, this.wounds + n);
    if (this.wounds === before) return false;   // already at the cap: silence
    this.hud.flashMove?.('You are bleeding!', { sev: 'warn', key: 'bleeding' });
    // NO SOUND HERE. Every wound() call site in the tree fires immediately
    // after a damage() call, and damage() now owns the pain grunt (see
    // _hurtFeedback) — so this played a SECOND take out of the same pool in
    // the same frame, and hurt.mjs's SOUND_GAP cannot see a play it did not
    // make. A wound is a warn toast plus the vignette damage() already drew.
    return true;
  }

  // bandage/duct tape: closes every open wound
  patch() {
    if (this.wounds <= 0) return false;
    this.wounds = 0;
    return true;
  }

  eat(def) {
    const fx = def.eat; // roasting swaps the item for its cookedId variant
    // medicine with no nutrition still has to act (antibiotics, clean rags)
    if (!fx) return this._medicate(def);
    this.hunger = Math.min(100, this.hunger + (fx.food || 0));
    this.thirst = Math.min(100, this.thirst + (fx.water || 0));
    if (fx.health) this.health = Math.min(100, this.health + fx.health);
    if (fx.poison) {
      this.poisons.push({ at: performance.now() + fx.poison.delay * 1000, dmg: fx.poison.dmg, applied: false });
    }
    this._medicate(def);
    return true;
  }

  // item-driven medical effects: cure:'infection' (antibiotics),
  // suppress:'symptoms' (painkillers), infectRisk (filthy rags)
  _medicate(def) {
    let acted = false;
    if (def.cure === 'infection') { this.cureInfection(); acted = true; }
    if (def.suppress === 'symptoms') { this.suppressSymptoms(); acted = true; }
    const risk = def.infectRisk ?? def.eat?.infectRisk;
    if (risk && Math.random() < risk) { this.infect(); acted = true; }
    return acted;
  }

  // THE one funnel every source of harm in the game passes through — wolf
  // bites, infected claws, PvP rounds, starvation, thirst, blood loss, poison
  // and sepsis — which is why the feedback belongs here and not at the call
  // sites. Before this it subtracted a number and played nothing, so four of
  // those eight sources had no representation on screen or in the mix at all.
  //
  // `src` is optional and every existing caller omits it:
  //   { kind, trickle, dir, heading }
  // `dir` is the world bearing the blow TRAVELS (atan2(dx, dz), attacker →
  // victim) — the same value infected-sim.mjs already sends and main.js
  // already blocks against — and `heading` is player.heading. Give both and
  // the hit gets a directional arc; give neither and it stays omnidirectional.
  damage(n, src = null) {
    if (this.dead) return;
    const amt = Number(n);
    if (!(amt > 0)) return;
    this.health = Math.max(0, this.health - amt);
    const ev = this.hurtFeed.report(amt, performance.now(), this.health, src);
    if (ev) this._hurtFeedback(ev);
    if (this.health <= 0) this.die();
  }

  _hurtFeedback(ev) {
    this.hud?.hurt?.(ev);
    // The numeric readout is otherwise driven off the 4 Hz vitals tick, so in
    // the burst where the player actually reads it, it showed a value they no
    // longer had. setHealth() early-outs on an unchanged rounded value, so
    // calling it on the damage event itself costs nothing.
    this.hud?.setHealth?.(this);
    if (ev.sound) {
      // ui.hurt is the role the agenda assigns to this moment and it is NOT
      // in sfx-index.json yet — another workstream is adding the five missing
      // roles. Sfx.play() returns false for an unknown role precisely so a
      // caller can fall back ("callers can fall back to their procedural
      // sound this once"), so we ask for ui.hurt and use the curated
      // player.hurt until the index catches up. Delete the fallback then.
      // slice:0 meant a DISCRETE hit played the chosen take in full, and the
      // player.hurt pool is not uniform: five of its six takes are 0.29-0.46 s
      // but 528732_oof-qdo.ogg is 14.79 s (measured with ffprobe), so roughly
      // one infected claw in six started a fifteen-second grunt compilation
      // that nothing could stop, several of them overlapping in a mob. sfx.js
      // only windows a take when `slice > 0` AND the take is longer than
      // slice + 0.2, so 0.5 plays every short take whole and clips anything
      // long — including whatever a future curator adds to the role.
      const o = { vol: ev.vol, rate: ev.rate, jitter: 0.06, slice: ev.trickle ? 0.35 : 0.5 };
      const sfx = this.audio?.sfx;
      if (sfx?.play && !sfx.play('ui.hurt', o)) sfx.play('player.hurt', o);
    }
    this.onHurt?.(ev);
  }

  die() {
    if (this.dead) return;
    this.dead = true;
    this.player.dead || this.player.toggleDead?.();
    // a death scream carries 60 m and brings whatever killed you friends (Z27)
    const p = this.player?.root?.position;
    if (p) this.noise?.emit('scream', p.x, p.z);
    this.hud.flashMove?.(this.infection && this.infection.stage === 'critical'
      ? 'The infection killed you — press K to respawn'
      : 'You died — press K to respawn', { sev: 'crit', key: 'died' });
  }

  respawn() {
    this.dead = false;
    // a fresh body does not inherit the last one's pooled trickle, or the
    // first step after a respawn pulses for damage taken before it
    this.hurtFeed.reset();
    this.health = 100;
    this.hunger = 70;
    this.thirst = 70;
    this.blood = 100;
    this.wounds = 0;
    this.poisons = [];
    this.infection = null;
    this.shakes = 0;
  }

  // persistence: main.js snapshot()/onWelcome carry these across sessions
  toJSON() {
    const o = { health: this.health, hunger: this.hunger, thirst: this.thirst, blood: this.blood, wounds: this.wounds };
    if (this.infection) {
      // store the onset as seconds-from-now so a server restart or a long
      // logout does not teleport the fever forward
      o.infection = { in: +((this.infection.at - performance.now()) / 1000).toFixed(1),
        sev: +this.infection.sev.toFixed(3) };
    }
    return o;
  }

  restore(o) {
    if (!o) return;
    const num = (v, d) => (Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : d);
    this.health = num(o.health, this.health);
    this.hunger = num(o.hunger, this.hunger);
    this.thirst = num(o.thirst, this.thirst);
    this.blood = num(o.blood, this.blood);
    this.wounds = Number.isFinite(o.wounds) ? Math.max(0, Math.min(5, o.wounds)) : this.wounds;
    if (o.infection) {
      const sev = Math.max(0, Math.min(1, Number(o.infection.sev) || 0));
      const secs = Number.isFinite(o.infection.in) ? o.infection.in : 0;
      this.infection = { at: performance.now() + secs * 1000, sev,
        stage: secs > 0 && sev <= 0 ? 'incubating' : 'fever', quietUntil: 0 };
    }
  }

  update(dt, sprinting) {
    if (this.dead) return;
    // full → empty: hunger ~40 min, thirst ~28 min (sprint drains ~2×)
    const mul = sprinting ? 2 : 1;
    this.hunger = Math.max(0, this.hunger - (100 / 2400) * mul * dt);
    this.thirst = Math.max(0, this.thirst - (100 / 1700) * mul * dt);
    if (this.hunger <= 0) this.damage(1.1 * dt, DAMAGE_SRC.hunger);
    if (this.thirst <= 0) this.damage(1.6 * dt, DAMAGE_SRC.thirst);

    // bleeding: each open wound drains ~0.6 blood/s (sprint pumps it faster);
    // patched and well-fed, blood slowly regrows
    if (this.wounds > 0) {
      this.blood = Math.max(0, this.blood - this.wounds * 0.6 * (sprinting ? 1.5 : 1) * dt);
    } else if (this.blood < 100 && this.hunger > 30 && this.thirst > 30) {
      this.blood = Math.min(100, this.blood + 0.25 * dt);
    }
    if (this.blood < 30) this.damage((30 - this.blood) * 0.05 * dt, DAMAGE_SRC.blood); // shock
    if (this.blood <= 0) this.damage(4 * dt, DAMAGE_SRC.blood);     // bled out

    const t = performance.now();
    for (const p of this.poisons) {
      if (!p.applied && t >= p.at) {
        p.applied = true;
        this.damage(p.dmg, DAMAGE_SRC.poison);
        // the flash and the sound now ride damage() itself (hurt.mjs); this
        // line is the words, and it dedupes so a double dose is one row
        this.hud.flashMove?.('You feel very ill…', { sev: 'warn', key: 'poison' });
      }
    }
    this.poisons = this.poisons.filter(p => !p.applied);

    this._infectionTick(dt);
  }
}
